import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { operationsRpc } from '@/lib/operations';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import { useOperations } from './useOperations';
import type { JerseyEntryContext } from '@klothiq/domain';
import { useEffect, useState } from 'react';

export function useJerseyDraftCode(
  input: {
    profileId: string;
    configurationVersion: string;
    draftKey: string;
    prefix: string;
    name: string;
    details: import('@klothiq/domain').JerseyDetailsV2;
  },
  enabled: boolean,
) {
  const { organization, profile, access } = useAuth();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(false);
    if (!enabled) return;
    const timer = setTimeout(() => setReady(true), 300);
    return () => clearTimeout(timer);
  }, [enabled, input.name, input.prefix, input.profileId, input.configurationVersion]);
  return useQuery({
    queryKey: [
      'jersey_draft_code',
      organization?.id,
      profile?.id,
      access?.access_version,
      input.profileId,
      input.configurationVersion,
      input.draftKey,
      input.prefix,
    ],
    enabled: enabled && ready,
    retry: false,
    staleTime: Infinity,
    queryFn: async () => {
      const result = await operationsRpc<{ contract_version: number; product_code: string }>(
        'get_jersey_draft_code_v2',
        {
          p_data: {
            contract_version: 2,
            profile_id: input.profileId,
            configuration_version_id: input.configurationVersion,
            draft_key: input.draftKey,
            name: input.name,
            details: input.details,
          },
        },
      );
      if (result.contract_version !== 2 || !result.product_code)
        throw new Error(
          'The generated code could not be confirmed. Retry or enter a code manually.',
        );
      return result;
    },
  });
}

export function useJerseyEntry(profileId?: string, productId?: string) {
  const { organization, profile, access } = useAuth();
  const operations = useOperations();
  return useQuery({
    queryKey: [
      'jersey_entry',
      organization?.id,
      profile?.id,
      access?.access_version,
      profileId,
      productId,
    ],
    enabled: !!organization && !!profileId && operations.data?.modules.jersey_entry === 2,
    retry: false,
    queryFn: async () => {
      const occupied: JerseyEntryContext['occupied_variants'] = [];
      let offset: number | null = 0;
      let context: JerseyEntryContext;
      do {
        const requestedOffset = offset;
        context = await operationsRpc<JerseyEntryContext>('get_jersey_entry_context_v2', {
          p_profile_id: profileId,
          p_product_id: productId || null,
          p_offset: offset,
        });
        if (
          context.contract_version !== 2 ||
          !Array.isArray(context.occupied_variants) ||
          (context.next_offset !== null &&
            (!Number.isInteger(context.next_offset) || context.next_offset <= requestedOffset))
        )
          throw new Error(
            'The jersey entry context is incompatible. Refresh or contact your administrator.',
          );
        occupied.push(...context.occupied_variants);
        offset = context.next_offset;
      } while (offset !== null);
      return { ...context, occupied_variants: occupied };
    },
  });
}

export interface JerseyEntrySaveResult {
  success: boolean;
  contract_version: 2;
  product_id: string;
  name: string;
  product_code: string;
  variant_ids: string[];
  variants: { id: string; sku: string; updated_at: string }[];
}
export function useJerseyEntrySave(operation = 'save_jersey_workspace_v2') {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      const result = await operationsRpc<JerseyEntrySaveResult>(operation, {
        p_data: { ...input, contract_version: 2 },
      });
      if (!result?.success || result.contract_version !== 2)
        throw new Error('The save could not be confirmed. Recover before retrying.');
      return result;
    },
    onSuccess: async () => {
      await refreshSaleConsumers(client);
      await client.invalidateQueries({ queryKey: ['jersey_entry'] });
    },
  });
}
